import { Redirect, useRouter } from 'expo-router'
import { FormattedMessage } from 'react-intl'

import { useOtpAuthentication } from '@/src/features/auth/services/useOtpAuthentication'
import { ErrorMessage } from '@/src/general/components/ErrorMessage'
import { TEST_ID } from '@/src/general/constants/testId'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { AuthDetailsScreen } from '../components/AuthDetailsScreen'
import { AuthInput } from '../components/AuthInput'
import { useOtpAuthenticationSession } from '../context/OtpAuthenticationContext'

type TNameScreenProps = {
  redirectTo?: string
}

export const NameScreen = ({ redirectTo }: TNameScreenProps) => {
  const session = useOtpAuthenticationSession()
  const router = useRouter()
  const { routes } = useRoutes()
  const { t } = useTranslate()
  const destination = redirectTo || routes['prayer-space']()
  const { initializationMutation, errors } = useOtpAuthentication(destination)

  if (!session.email || session.mode !== 'signup') return <Redirect href={routes.authConnect()} />

  const submitName = async () => {
    const normalizedName = session.name.trim()
    if (!normalizedName) return

    const csrfToken = await initializationMutation.mutateAsync({ mode: 'signup', email: session.email })
    session.updateSession({ name: normalizedName, csrfToken, nameErrorKey: undefined })
    router.push(routes.authOtp({ redirectTo: destination }))
  }

  const handleNameChange = (value: string) => {
    session.updateSession({ name: value, nameErrorKey: undefined })
    initializationMutation.reset()
  }

  return (
    <AuthDetailsScreen
      title={<FormattedMessage id="auth.otp.signup.title" />}
      isPending={initializationMutation.isPending}
      isSubmitDisabled={!session.name.trim()}
      onSubmit={() => void submitName().catch(() => {})}
    >
      <AuthInput
        testID={TEST_ID.ME.AUTHENTICATION.OTP_NAME_INPUT}
        placeholder={t('word.your-firstname')}
        value={session.name}
        onChangeText={handleNameChange}
        onSubmitEditing={() => void submitName().catch(() => {})}
        autoFocus
        errorMessage={session.nameErrorKey ? t(session.nameErrorKey) : undefined}
        autoCapitalize="words"
        returnKeyType="done"
        maxLength={50}
      />
      {errors.initialization.generalKey && <ErrorMessage defaultErrorKey={errors.initialization.generalKey} />}
    </AuthDetailsScreen>
  )
}
